import { motion, useScroll, useTransform } from "framer-motion";
import { FC, ReactNode, useRef } from "react";
import { useMediaQuery } from "@tennr/frontend/hooks/useMediaQuery";
import "./Fractals.css";

interface FractalsProps {
  variant?: "about" | "home" | "product" | "studies" | "studies-detail" | "book";
  className?: string;
  children?: ReactNode;
}

const svgPaths = {
  about: {
    p1: [
      "/svg/home-animation/about/r1.svg",
      "/svg/home-animation/about/r2.svg",
      "/svg/home-animation/about/r3.svg",
      "/svg/home-animation/about/r4.svg",
    ],
    p2: [
      "/svg/home-animation/about/p2/Rectangle 24198.svg",
      "/svg/home-animation/about/p2/Rectangle 24199.svg",
      "/svg/home-animation/about/p2/Rectangle 24200.svg",
      "/svg/home-animation/about/p2/Rectangle 24201.svg",
    ],
    mobile: "/svg/home-animation/about/mobile.svg",
    desktop: null, // About uses individual rectangles
  },
  // Placeholder for other variants
  home: { p1: [], p2: [], mobile: "", desktop: null },
  product: { p1: [], p2: [], mobile: "", desktop: null },
  // New variants with single SVGs
  studies: {
    p1: [],
    p2: [],
    mobile: "/svg/home-animation/studies/mobile.svg",
    desktop: "/svg/home-animation/studies/desktop.svg",
  },
  "studies-detail": {
    p1: [],
    p2: [],
    mobile: "/svg/home-animation/studies-detail/mobile.svg",
    desktop: "/svg/home-animation/studies-detail/desktop.svg",
  },
  book: {
    p1: [],
    p2: [],
    mobile: "/svg/home-animation/book/mobile.svg",
    desktop: "/svg/home-animation/book/desktop.svg",
  },
};

export const Fractals: FC<FractalsProps> = ({ variant = "about", className = "", children }) => {
  const paths = svgPaths[variant];
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Scroll-based parallax
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Create parallax offsets for each rectangle - 2x range
  const parallaxOffsets = [
    useTransform(scrollYProgress, [0, 1], [0, -20]),
    useTransform(scrollYProgress, [0, 1], [0, -30]),
    useTransform(scrollYProgress, [0, 1], [0, -40]),
    useTransform(scrollYProgress, [0, 1], [0, -50]),
    // Second set with different offsets
    useTransform(scrollYProgress, [0, 1], [0, -25]),
    useTransform(scrollYProgress, [0, 1], [0, -35]),
    useTransform(scrollYProgress, [0, 1], [0, -45]),
    useTransform(scrollYProgress, [0, 1], [0, -55]),
  ];

  // Animation variants for stark, geometric entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.4, // 400ms delay after page load
        staggerChildren: 0.05, // Minimal stagger for snappy feel
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.25, // 250ms fade in
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      ref={containerRef}
      className={`fractals ${className}`}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Clipping mask wrapper - clips vertically but allows horizontal overflow */}
      <div className="fractals-clip-mask">
        <div className="fractals-layer fractals-p1">
          {isMobile ? (
            // Mobile: Single SVG
            <motion.div
              className="fractal-shape-wrapper fractal-mobile"
              variants={itemVariants}
              style={{
                y: parallaxOffsets[0], // Use first parallax offset
              }}
            >
              <img
                src={paths.mobile}
                alt=""
                className="fractal-shape"
                style={{
                  mixBlendMode: "multiply",
                  opacity: 0.5,
                }}
                draggable={false}
              />
            </motion.div>
          ) : // Desktop: Either single SVG or individual rectangles
          paths.desktop ? (
            // Single desktop SVG
            <motion.div
              className="fractal-shape-wrapper fractal-desktop"
              variants={itemVariants}
              style={{
                y: parallaxOffsets[0], // Use first parallax offset
              }}
            >
              <img
                src={paths.desktop}
                alt=""
                className="fractal-shape"
                style={{
                  mixBlendMode: "multiply",
                  opacity: 0.5,
                }}
                draggable={false}
              />
            </motion.div>
          ) : (
            // Individual rectangles (for about page)
            paths.p1.map((path, index) => (
              <motion.div
                key={`p1-${index}`}
                className={`fractal-shape-wrapper fractal-shape-${index % 4} fractal-set-${Math.floor(index / 4)}`}
                variants={itemVariants}
                style={{
                  y: parallaxOffsets[index],
                }}
              >
                <img
                  src={path}
                  alt=""
                  className="fractal-shape"
                  style={{
                    mixBlendMode: "multiply",
                    opacity: 0.5,
                  }}
                  draggable={false}
                />
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Children content on top */}
      {children && <div className="fractals-content">{children}</div>}
    </motion.div>
  );
};
